import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Download, FileText, Mail, FileCode, Table, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const EXPORT_FORMATS = [
  {
    value: 'pdf',
    label: 'PDF Document',
    description: 'Professional PDF for sharing or printing',
    icon: FileText,
    color: 'red'
  },
  {
    value: 'markdown',
    label: 'Markdown',
    description: 'Plain text with formatting',
    icon: FileCode,
    color: 'blue'
  },
  {
    value: 'email',
    label: 'Email Format (HTML)',
    description: 'Ready-to-send email template',
    icon: Mail,
    color: 'green'
  },
  {
    value: 'csv',
    label: 'Tasks as CSV',
    description: 'Export action items as spreadsheet',
    icon: Table,
    color: 'orange'
  }
];

const ExportModal = ({ isOpen, onClose, documentId, documentTitle }) => {
  const { toast } = useToast();
  const [exporting, setExporting] = useState(false);

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/work/documents/${documentId}/export?format=${format}`,
        {
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error('Export failed');
      }

      // Get filename from headers or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `${documentTitle}.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }

      // Download file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: '✅ Export Successful!',
        description: `Downloaded ${filename}`
      });

      onClose();
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: 'Export Failed',
        description: 'Unable to export document. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Export Document</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-4">
          <p className="text-sm text-gray-600">Choose export format:</p>
          {EXPORT_FORMATS.map((format) => {
            const Icon = format.icon;
            return (
              <Button
                key={format.value}
                onClick={() => handleExport(format.value)}
                disabled={exporting}
                variant="outline"
                className="w-full justify-start h-auto py-3 hover:bg-gray-50"
              >
                <div className="flex items-start gap-3 w-full">
                  <Icon className={`h-5 w-5 text-${format.color}-600 flex-shrink-0 mt-0.5`} />
                  <div className="text-left flex-1">
                    <p className="font-medium text-gray-900">{format.label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{format.description}</p>
                  </div>
                  {exporting ? (
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                  ) : (
                    <Download className="h-4 w-4 text-gray-400" />
                  )}
                </div>
              </Button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExportModal;
